import { useTranslation } from 'react-i18next';
import { Flame, LayoutGrid } from 'lucide-react';
import CategoryIcon from './CategoryIcon';
import { POS_CATEGORY_SELECTED_CLASS } from '@/constants/posCategoryIcons';
import type { POSCategory } from '@/types';

interface Props {
  categories: POSCategory[];
  activeCategory: string;
  onSelect: (name: string) => void;
  position: 'top' | 'bottom';
  showHotSelling?: boolean;
  viewMode?: 'products' | 'services';
  className?: string;
}

export default function PosCategoryBar({
  categories,
  activeCategory,
  onSelect,
  position,
  showHotSelling = true,
  viewMode = 'products',
  className = '',
}: Props) {
  const { t } = useTranslation();

  const wrapperClasses =
    position === 'bottom'
      ? 'border-t border-gray-200 bg-white p-3'
      : 'border-b border-gray-200 bg-white p-3';

  return (
    <div className={`${wrapperClasses} ${className}`}>
      <div className="flex items-center gap-2 overflow-x-auto pb-1 hide-scrollbar">
        {showHotSelling && viewMode === 'products' && (
          <button
            type="button"
            onClick={() => onSelect('__recent__')}
            className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors min-h-[48px] ${
              activeCategory === '__recent__'
                ? `bg-orange-500 text-white ${POS_CATEGORY_SELECTED_CLASS}`
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Flame className="w-4 h-4" />
            {t('pos.hotSelling')}
          </button>
        )}
        <button
          type="button"
          onClick={() => onSelect('All')}
          className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors min-h-[48px] ${
            activeCategory === 'All'
              ? `bg-primary-600 text-white ${POS_CATEGORY_SELECTED_CLASS}`
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <LayoutGrid className="w-4 h-4" />
          {t('common.all')}
        </button>
        {categories.map((cat) => {
          const selected = activeCategory === cat.name;
          const bg = cat.pos_category_color || (selected ? '#2563eb' : '#f3f4f6');
          const colored = Boolean(cat.pos_category_color) || selected;
          return (
            <button
              key={cat.name}
              type="button"
              onClick={() => onSelect(cat.name)}
              className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors min-h-[48px] hover:opacity-90 ${
                colored ? 'text-white' : 'text-gray-700'
              } ${selected ? POS_CATEGORY_SELECTED_CLASS : ''}`}
              style={{ backgroundColor: bg }}
              aria-pressed={selected}
            >
              <CategoryIcon
                icon={cat.pos_category_icon}
                image={cat.pos_category_image}
                color={colored ? 'rgba(255,255,255,0.22)' : '#e5e7eb'}
                foreground={colored ? '#ffffff' : undefined}
                size="sm"
              />
              <span className="truncate max-w-[120px]">{cat.category_name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
